import path from "path";
import required from "@libs/required";
import { isBrowser } from "@utils/Browser";
import hydrate from "./src/libs/hydrate";
import getResources from "./src/libs/getResources.js";

const { NODE_ENV, SITE_NAME, ENV, VERSION, PAGE_BUNDLE_PATH } = process.env;
console.log({ VERSION, SITE_NAME });

// import { datadogRum } from "@datadog/browser-rum";

// if (NODE_ENV !== "development") {
// 	datadogRum.init({
// 		silentMultipleInit: true,
// 		clientToken: "pubc7bbeec4b2aece95dad4ee5ef70ff36c",
// 		applicationId: "dcf52881-52a3-44bc-bff7-58c5c37bafdb",
// 		site: "datadoghq.com",
// 		service: "react-app",
// 		env: ENV,
// 		version: VERSION || "0.0.1",
// 		sampleRate: 0,
// 		sessionReplaySampleRate: 0,
// 		sessionSampleRate: 0,
// 		trackResources: true,
// 		trackLongTasks: true,
// 		trackInteractions: true,
// 	});

// 	datadogRum.startSessionReplayRecording();
// }

class Bundle {
	req;
	values;
	routes;

	static generatePaths() {}

	constructor(req) {
		this.req = req;
		if (PAGE_BUNDLE_PATH) {
			this.values = isBrowser ? ["."] : PAGE_BUNDLE_PATH.split(",");
		} else if (isBrowser) {
			const appStateElem = document.querySelector("#app-page-state");
			const staticContext = JSON.parse(appStateElem?.innerHTML || "{}") || {};
			var { currentRoutePath, currentRouteBundlePath } = staticContext;
			if (!currentRoutePath && NODE_ENV !== "test") {
				throw new Error("currentRoutePath must be set in the staticContext");
			}
			if (currentRouteBundlePath) this.values = [currentRouteBundlePath];
			else {
				const paths = this.req.keys();
				this.values = paths.map((p) => path.dirname(p).replace("./", ""));
			}
		} else {
			const paths = this.req.keys();
			this.values = paths.map((p) => path.dirname(p).replace("./", ""));
		}

		this.routes = [];
		for (const bundlePath of this.values) {
			const { routes: asset } = this.findBundleByRoute({ bundlePath });
			if (!asset) continue;
			const routes = asset
				.keys()
				.map((p) => asset(p).default)
				.flat()
				.filter(Boolean);
			routes.forEach((route) => {
				const existingRouteIndex = this.routes.findIndex(
					(existingRoute) => existingRoute.path === route.path
				);
				if (existingRouteIndex === -1) {
					this.routes.push({
						...route,
						__componentPaths: routes.map(
							(route) => route.componentPath || bundlePath
						),
						ssr: route.ssr || route.seo,
						componentPath: route.componentPath || bundlePath,
						bundlePath: route.bundlePath || bundlePath,
						loadablePath: PAGE_BUNDLE_PATH
							? bundlePath
							: bundlePath === "."
							? "ROOT_BUNDLE"
							: bundlePath,
					});
				} else {
					this.routes[existingRouteIndex] = {
						...this.routes[existingRouteIndex],
						__componentPaths: routes.map(
							(route) => route.componentPath || bundlePath
						),
						ssr: route.ssr || route.seo,
						componentPath: route.componentPath || bundlePath,
						bundlePath: route.bundlePath || bundlePath,
					};
				}
			});
		}
		if (isBrowser && NODE_ENV !== "test") {
			const route = this.routes.find(
				(route) => route.path === currentRoutePath
			);
			hydrate(this.generate(route));
		}
	}

	__getPaths(
		asset = required`asset`,
		route = required`route`,
		routesAsset = required`routesAsset`
	) {
		const bundleRoutes = routesAsset
			.keys()
			.map((p) => routesAsset(p).default)
			.flat();

		if (bundleRoutes.length === 1) {
			return asset.keys();
		}

		const filterNonRelevantPaths = (paths) =>
			paths.filter((p) => {
				const otherRoutes = this.routes.filter(
					(r) => r.path !== route.path && r.component !== route.component
				);
				const conflictingRoute = otherRoutes.find((route) => {
					return p.indexOf(`./${path.dirname(route.component)}/`) === 0;
				});
				return !conflictingRoute;
			});

		const componentPath = `./${route.component}`; //`${route.bundlePath}/${route.component}`;
		const paths = asset.keys().reduce((result, path) => {
			const slashCount = path.split("/").length;
			if (!result[slashCount]) result[slashCount] = [];
			result[slashCount].push(path);
			return result;
		}, {});

		let maxIndex = Object.keys(paths).find((index) => {
			return paths[index].find(
				(p) =>
					path.dirname(path.dirname(p)).replace("./", "") ===
					path.dirname(componentPath)
			);
		});

		if (maxIndex === undefined)
			maxIndex = route.component.split("/").length + 2;

		let targetPaths = (paths[maxIndex] || []).filter(
			(p) =>
				path.dirname(path.dirname(p)).replace("./", "") ===
				path.dirname(route.component)
		);

		let parentPaths = Object.keys(paths)
			.filter((index) => parseInt(index) < maxIndex)
			.map((index) => paths[index])
			.flat();

		let childPaths = asset
			.keys()
			.filter((p) => p.indexOf(path.dirname(componentPath) + "/") === 0);

		targetPaths = filterNonRelevantPaths(targetPaths);
		parentPaths = filterNonRelevantPaths(parentPaths);
		childPaths = filterNonRelevantPaths(childPaths);

		return Array.from(
			new Set([...parentPaths, ...targetPaths, ...childPaths])
		).filter(Boolean);
	}

	__assetsWrapper(assets) {
		function wrapper(targetPath) {
			return assets.find(({ path }) => targetPath === path)?.value;
		}
		wrapper.keys = () => assets.map((asset) => asset.path);
		return wrapper;
	}

	__findPathsByRoute(route, { routesAsset, serverAssets, clientAssets }) {
		let fn;
		if (isBrowser) {
			fn = this.__assetsWrapper(clientAssets);
		} else {
			fn = serverAssets;
		}

		return this.__getPaths(fn, route, routesAsset).map((p) => {
			return {
				value: fn(p).default || (isBrowser ? fn(p) : fn(p)),
				name: path.basename(p),
			};
		});
	}

	findStylesByRoute(route = required`route`) {
		const {
			styles: clientStyles,
			serverStyles,
			routes: routesAsset,
		} = this.findBundleByRoute(route);
		if (!clientStyles && !serverStyles) return;
		return this.__findPathsByRoute(route, {
			routesAsset,
			serverAssets: serverStyles,
			clientAssets: clientStyles,
		}).reduce(
			(result, { value, name }) =>
				Object.assign(result, {
					[name.replace(".styles.js", "")]: isBrowser
						? { value, __preInitialized: true }
						: value,
				}),
			{}
		);
	}

	findStoresByRoute(route = required`route`) {
		const {
			stores: clientStores,
			serverStores,
			routes: routesAsset,
		} = this.findBundleByRoute(route);
		if (!clientStores && !serverStores) return;

		const paths = this.__findPathsByRoute(route, {
			routesAsset,
			serverAssets: serverStores,
			clientAssets: clientStores,
		});

		return paths.reduce(
			(result, { value, name }) =>
				Object.assign(result, {
					[name.replace(".store.js", "Store")]: isBrowser
						? { value, __preInitialized: true }
						: value,
				}),
			{}
		);
	}

	findLoadableComponentByRoute(route = required`route`) {
		const { loadable: asset } = this.findBundleByRoute(route) || {};
		if (!asset) return;
		try {
			const component = asset(`./${route.component}.loadable.js`);
			// if (route.ssr && !isBrowser) component.default(route).then();
			return component.default;
		} catch (err) {
			return;
		}
	}

	findConstantsByRoute(route = required`route`) {
		const { serverConstants, constants: clientConstants } =
			this.findBundleByRoute(route);
		if (!serverConstants && !clientConstants) return;
		if (serverConstants)
			return serverConstants
				.keys()
				.filter(
					(p) =>
						~p.indexOf(`${SITE_NAME}/`) ||
						p.split("@constants")?.reverse()[0]?.split("/").length === 2
				)
				.map((p) => ({
					name: path.basename(p, ".json"),
					value: serverConstants(p),
				}))
				.reduce(
					(result, { name, value }) => Object.assign(result, { [name]: value }),
					{}
				);

		return clientConstants
			.filter(
				({ path: p }) =>
					~p.indexOf(`${SITE_NAME}/`) ||
					p.split("@constants")?.reverse()[0]?.split("/").length === 2
			)
			.reduce((result, { path: p, value }) => {
				const name = path.basename(p, ".json");
				return Object.assign(result, {
					[name]: { value, __preInitialized: true },
				});
			}, {});
	}

	findBackgroundByRoute(route = required`route`) {
		const { background: asset, routes: routesAsset } =
			this.findBundleByRoute(route);
		if (!asset) return;

		const response = this.__getPaths(asset, route, routesAsset);

		if (response.length === 0) return;
		const { default: value, props } = asset(response[response.length - 1]);
		return { value, props };
	}

	findControllersByRoute(route = required`route`) {
		const {
			controllers: clientControllers,
			serverControllers,
			routes: routesAsset,
		} = this.findBundleByRoute(route);
		if (!clientControllers && !serverControllers) return;

		return this.__findPathsByRoute(route, {
			serverAssets: serverControllers,
			clientAssets: clientControllers,
			routesAsset,
		}).map(({ value }) =>
			isBrowser ? { value, __preInitialized: true } : value
		);
	}

	findExtensionsByRoute(route = required`route`) {
		const {
			extensions: clientExtensions,
			serverExtensions,
			routes: routesAsset,
		} = this.findBundleByRoute(route);

		if (!clientExtensions && !serverExtensions) return;

		const response = this.__findPathsByRoute(route, {
			routesAsset,
			serverAssets: serverExtensions,
			clientAssets: clientExtensions,
		});

		if (response.length === 0) return;
		const { value } = response[0];

		return isBrowser ? { value, __preInitialized: true } : value;
	}

	findModelsByRoute(route = required`route`) {
		const {
			models: clientModels,
			serverModels,
			routes: routesAsset,
		} = this.findBundleByRoute(route);
		if (!serverModels && !clientModels) return;

		return this.__findPathsByRoute(route, {
			serverAssets: serverModels,
			clientAssets: clientModels,
			routesAsset,
		}).map(({ value }) =>
			isBrowser ? { value, __preInitialized: true } : value
		);
	}

	findMetaByRoute(route = required`route`) {
		const { meta: asset, routes: routesAsset } = this.findBundleByRoute(route);

		if (!asset) return;

		const response = this.__getPaths(asset, route, routesAsset)
			.filter((p) => path.basename(p, ".js").toLowerCase() === SITE_NAME)
			.map((path) => asset(path).default);

		return response;
	}

	findLibsByRoute(route = required`route`) {
		const {
			libs: clientLibs,
			serverLibs,
			routes: routesAsset,
		} = this.findBundleByRoute(route);
		if (!clientLibs && !serverLibs) return;

		return this.__findPathsByRoute(route, {
			serverAssets: serverLibs,
			clientAssets: clientLibs,
			routesAsset,
		}).reduce(
			(result, { value, name }) =>
				Object.assign(result, {
					[name.replace(".js", "")]: isBrowser
						? { value, __preInitialized: true }
						: value,
				}),
			{}
		);
	}

	findBundleByRoute(route = required`route`) {
		try {
			if (route.bundlePath === ".") return this.req("./@.bundle.js");
			return this.req(`./${route.bundlePath}/@.bundle.js`);
		} catch (err) {
			return {};
		}
	}

	async findRouteByPath(path, overrideOptions = {}) {
		try {
			const routes = this.generate();
			const currentRoute = routes.find((route) => route.path === path);
			const route = await getResources({ currentRoute });
			return Object.assign({}, route, overrideOptions);
		} catch (err) {
			throw err;
		}
	}

	generate(route) {
		if (route?.seo && !route?.ssr) {
			return [
				{
					...route,
					stores: this.findStoresByRoute(route),
					model: this.findModelsByRoute(route),
					styles: this.findStylesByRoute(route),
					controller: this.findControllersByRoute(route),
					constants: this.findConstantsByRoute(route),
					background: this.findBackgroundByRoute(route),
					libs: this.findLibsByRoute(route),
					meta: this.findMetaByRoute(route),
					extensions: this.findExtensionsByRoute(route),
					component:
						this.findLoadableComponentByRoute(route) || route.component,
				},
			];
		}

		const routes = this.routes.map((route) => ({
			...route,
			stores: this.findStoresByRoute(route),
			model: this.findModelsByRoute(route),
			styles: this.findStylesByRoute(route),
			controller: this.findControllersByRoute(route),
			constants: this.findConstantsByRoute(route),
			background: this.findBackgroundByRoute(route),
			libs: this.findLibsByRoute(route),
			meta: this.findMetaByRoute(route),
			extensions: this.findExtensionsByRoute(route),
			component: this.findLoadableComponentByRoute(route) || route.component,
		}));
		return routes;
	}
}

export default Bundle;
